import React, { memo, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import css from './toolbar.module.css';
import { ListParams } from '../../../../../services/http-client';
import { useService } from '../../../../../services/services';
import DateFilter, {
  DateFields,
} from '../../../../../components/common/table/date-filter/date-filter';
import ExcelButtonNew from '../../../../../components/common/table/excel-button/excel-button-new';
import AnimalListCreateButtons from './create-buttons/animal-list-create-buttons';
import ValueFilter from '../../../../../components/common/table/value-filter/value-filter';
import ShowFilter from '../../../../../components/common/table/show-filter/show-filter';
import ShowIfAllowed from '../../../../../components/common/show-if-allowed';
import ShowSettings from '../../../../../components/common/table/show-settings/show-settings';
import SettingsModal from './settings-modal';

const DEBOUNCE_TIME = 3000;

function Toolbar({ params }: { params: ListParams }) {
  const { t } = useTranslation();
  const transKeyPrefix = 'pages.livestock.animals_and_events.animals.';
  const tp = useCallback((key: string) => t(transKeyPrefix.concat(key)), [t]);
  const { farmId } = useParams<{ farmId: string }>();
  const { animalService, animalConfigurationService } = useService();
  const [species, setSpecies] = useState([]);
  const [animalGroups, setAnimalGroups] = useState([]);
  const [genders, setGenders] = useState([]);
  const [breeds, setBreeds] = useState([]);
  const [barns, setBarns] = useState([]);
  const [animalLdcNumbers, setAnimalLdcNumbers] = useState([]);
  const [animalEids, setAnimalEids] = useState([]);
  const [showFilter, setShowFilter] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const [isDataGenerated, setIsDataGenerated] = useState(false);

  const closeModal = () => {
    setShowSettings(false);
    setIsDataGenerated(false);
  };

  useEffect(() => {
    const $species = animalService
      .species(farmId, params?.params)
      .subscribe((species) => setSpecies(species));
    const $animalGroups = animalService
      .animalGroups(farmId, params?.params)
      .subscribe((animalGroups) => setAnimalGroups(animalGroups));
    const $genders = animalService
      .genders(farmId, params?.params)
      .subscribe((genders) => setGenders(genders));
    const $breeds = animalService
      .breeds(farmId, params?.params)
      .subscribe((breeds) => setBreeds(breeds));
    const $barns = animalService
      .barns(farmId, params?.params)
      .subscribe((barns) => setBarns(barns));
    const $animalLdcNumbers = animalService
      .animalLdcNumbers(farmId, params?.params)
      .subscribe((animalLdcNumbers) => setAnimalLdcNumbers(animalLdcNumbers));
    const $animalEids = animalService
      .animalEids(farmId, params?.params)
      .subscribe((animalEids) => setAnimalEids(animalEids));

    return () => {
      $species.unsubscribe();
      $animalGroups.unsubscribe();
      $genders.unsubscribe();
      $breeds.unsubscribe();
      $barns.unsubscribe();
      $animalLdcNumbers.unsubscribe();
      $animalEids.unsubscribe();
    };
  }, [animalService, farmId, params]);

  useEffect(() => {
    animalConfigurationService
      .getTableData(farmId)
      .subscribe((response: boolean) => {
        if (response) {
          setIsDataGenerated(true);
        }
      });
  }, [animalConfigurationService, farmId]);

  return (
    <div className={css['animal-list-topbar']}>
      <ShowIfAllowed ability={{ title: 'create animal_event' }}>
        <div className={css['event-bar']}>
          <AnimalListCreateButtons farmId={farmId} animals={params.animals} />
        </div>
      </ShowIfAllowed>

      <div className={css['filter-bar-excel']}>
        <div className={css['filter-show']}>
          <label htmlFor="show-filter" className={css['filter-label']}></label>
          <ShowFilter
            onClick={() => setShowFilter(!showFilter)}
            defaultActiveValue={true}
          />
        </div>

        <div>
          <label
            htmlFor="show-settings"
            className={css['filter-label']}
          ></label>
          <ShowSettings onClick={() => setShowSettings(!showSettings)} />
          {showSettings && <SettingsModal closeModal={closeModal} />}
        </div>
        {isDataGenerated && <SettingsModal closeModal={closeModal} />}

        <div className={css['excel-export']}>
          <label htmlFor="excel-export" className={css['filter-label']}></label>
          <ExcelButtonNew
            onClick={() => {
              const filter = { ...params.params };

              delete params.params;
              return animalService
                .exportXls(farmId, { ...filter, ...params })
                .subscribe();
            }}
          />
        </div>
      </div>
      {showFilter && (
        <div className={css['filter-bar']}>
          <div className={css['date-filter']}>
            <label htmlFor="date">{tp('animals_date')}</label>
            <DateFilter
              name={'animals-date'}
              fields={[DateFields.date]}
              showDateControls={false}
              defaultValue={new Date()}
              disableFutureDates={true}
              debounceTime={DEBOUNCE_TIME}
            />
          </div>

          <div className={css['species-filter']}>
            <label htmlFor="species" className={css['filter-label']}>
              {tp('species')}
            </label>
            <div className={css['species-filter-container']}>
              <ValueFilter
                name="species"
                options={species}
                className={'value-filter-width-160'}
                isMulti={true}
                showSelectedCountInsteadOfValue={true}
                closeMenuOnSelect={false}
              />
            </div>
          </div>

          <div className={css['animal-groups-filter']}>
            <label htmlFor="animal-groups" className={css['filter-label']}>
              {tp('animal_group')}
            </label>
            <div className={css['animal-groups-filter-container']}>
              <ValueFilter
                name="animal-groups"
                options={animalGroups}
                className={'value-filter-width-160'}
                isMulti={true}
                showSelectedCountInsteadOfValue={true}
                closeMenuOnSelect={false}
              />
            </div>
          </div>

          <div className={css['gender-filter']}>
            <label htmlFor="gender" className={css['filter-label']}>
              {tp('gender')}
            </label>
            <div className={css['gender-filter-container']}>
              <ValueFilter
                name="gender"
                options={genders}
                className={'value-filter-width-160'}
                isMulti={true}
                showSelectedCountInsteadOfValue={true}
                closeMenuOnSelect={false}
              />
            </div>
          </div>

          <div className={css['breed-filter']}>
            <label htmlFor="breed_code" className={css['filter-label']}>
              {tp('breed')}
            </label>
            <div className={css['breed-filter-container']}>
              <ValueFilter
                name="breed_code"
                options={breeds}
                className={'value-filter-width-160'}
                isMulti={true}
                showSelectedCountInsteadOfValue={true}
                closeMenuOnSelect={false}
              />
            </div>
          </div>

          <div className={css['barn-filter']}>
            <label htmlFor="barn_ldc_nr" className={css['filter-label']}>
              {tp('barn')}
            </label>
            <div className={css['barn-filter-container']}>
              <ValueFilter
                name="barn_ldc_nr"
                options={barns}
                className={'value-filter-width-160'}
                isMulti={true}
                showSelectedCountInsteadOfValue={true}
                closeMenuOnSelect={false}
              />
            </div>
          </div>

          <div className={css['animal-id-filter']}>
            <label htmlFor="animal_id" className={css['filter-label']}>
              {tp('animal_id')}
            </label>
            <div className={css['animal-id-filter-container']}>
              <ValueFilter
                name="animal_id"
                options={animalLdcNumbers}
                className={'value-filter-width-200'}
                isMulti={true}
                showSelectedCountInsteadOfValue={true}
                closeMenuOnSelect={false}
              />
            </div>
          </div>

          <div className={css['animal-eid-filter']}>
            <label htmlFor="animal_eid" className={css['filter-label']}>
              {tp('animal_eid')}
            </label>
            <div className={css['animal-eid-filter-container']}>
              <ValueFilter
                name="animal_eid"
                options={animalEids}
                className={'value-filter-width-160'}
                isMulti={true}
                showSelectedCountInsteadOfValue={true}
                closeMenuOnSelect={false}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default memo(Toolbar);
